import type { Option } from '@/domain/base';
import { arrayToMap, handleError } from '@/utils/utils';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse, GenericAbortSignal } from 'axios';
import { useMemo, useRef } from 'react';

class ResponseError extends Error {
  response?: AxiosResponse;

  jsonResponse: any;

  textResponse: string;

  displayMessage: string;

  status?: number;

  constructor(msg: string, response?: AxiosResponse) {
    super(msg);
    this.response = response;
    this.jsonResponse = {};
    this.textResponse = '';
    this.displayMessage = msg;
  }
}

type DefaultOptions = {
  errorMessage: string | undefined;
};

interface FetchHookOptions {
  disabled?: boolean;
  errorMessage?: string;
  refetchOnMount?: boolean;
  ignoreError?: (error: Error) => boolean;
}

type Result = any;
type QueryKey = any[];
type QueryByIdFn<Data, Params> = (params: Params, signal?: GenericAbortSignal) => Promise<Data>;
type QuerySearchFn<Data, Params> = (params: Params, signal?: GenericAbortSignal) => Promise<Data>;
type QueryNoParamsFn<Data> = (signal?: GenericAbortSignal) => Promise<Data>;
type QueryRegularFn<Data, Params> = (params: Params, signal?: GenericAbortSignal) => Promise<Data>;

interface OptionsSettings {
  includeNoneOption?: boolean;
}

type ParseToMapConfig<MapKey> = {
  getKey: (item: Result extends any[] ? Result[0] : Result) => MapKey;
  getValue?: (item: Result extends any[] ? Result[0] : Result) => Result[0];
};

type MapAndOptionsHookOptions<MapKey extends keyof any, OptionValue> = {
  parseToMap?: ParseToMapConfig<MapKey>;
  parseToOptions?: ParseToOptionsConfig<OptionValue>;
};

type ParseToOptionsConfig<OptionValue> = {
  getLabel: (item: Result extends any[] ? Result[0] : Result) => string;
  getValue?: (item: Result extends any[] ? Result[0] : Result) => OptionValue;
  noneOption?: Option<OptionValue>;
  sort?: boolean;
};

type SearchHookOptions<
  MapKey extends keyof any,
  OptionValue,
  Params extends object = object,
> = DefaultOptions &
  MapAndOptionsHookOptions<MapKey, OptionValue> & {
    disable?: (params: Partial<Params>) => boolean;
  };

type NoParamsHookOptions<MapKey extends keyof any, OptionValue> = DefaultOptions &
  MapAndOptionsHookOptions<MapKey, OptionValue>;

type RegularHookOptions<Params extends any[]> = DefaultOptions & {
  disable?: (params: Params) => boolean;
};

const DEFAULT_EMPTY_PARAMS = {};

const useOptionsSettings = <OptionValue = Result>(
  data: Result[] | undefined,
  config: ParseToOptionsConfig<OptionValue> | undefined,
  optionsSettings: OptionsSettings | undefined,
) => {
  const { getLabel, getValue, sort: optionsSort = true } = config || {};

  return useMemo<Option<OptionValue>[]>(() => {
    if (!getLabel || !getValue || !data) {
      return [];
    }

    const mappedOptions = data.map((item: Result) => ({
      label: getLabel(item),
      value: getValue(item),
    }));

    if (optionsSort) {
      mappedOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
    }

    if (optionsSettings?.includeNoneOption && config?.noneOption) {
      mappedOptions.unshift(config.noneOption);
    }

    return mappedOptions;
  }, [data, getLabel, getValue, optionsSort, optionsSettings, config?.noneOption]);
};

const useMapSettings = <MapKey extends keyof any = keyof any>(
  data: Result | undefined,
  config: ParseToMapConfig<MapKey> | undefined,
) => {
  const { getKey: mapKey, getValue: mapValue } = config ?? {};
  return useMemo<Partial<Record<MapKey, Result[0]>>>(() => {
    if (!mapKey || !data) {
      return {};
    }

    return arrayToMap(data, mapKey, mapValue);
  }, [data, mapKey, mapValue]);
};

const onError = (
  error: Error,
  defaultOptions: DefaultOptions,
  signal?: AbortSignal,
  options?: FetchHookOptions,
) => {
  if (signal?.aborted) {
    throw error;
  }
  if (!options?.ignoreError?.(error)) {
    const message = options?.errorMessage ?? defaultOptions.errorMessage;
    handleError(error, {
      displayToast: !!message,
      toastMessage: message,
    });
  }
  return false;
};

const useEnrichedQuery = <Data, Params>(
  queryKey: QueryKey,
  queryFn: (params: Params, signal?: AbortSignal) => Promise<Data>,
  params: Params,
  disabledParam: boolean,
  options: FetchHookOptions,
  defaultOptions: DefaultOptions,
) => {
  const { disabled } = options;

  const controllerRef = useRef<AbortSignal>();

  const currentQueryKey = [...queryKey, params];

  return useQuery({
    queryKey: currentQueryKey,
    queryFn: (context) => {
      controllerRef.current = context.signal;
      return queryFn(params, context.signal).then((data) => {
        if (controllerRef?.current?.aborted) {
          throw new Error('Fetch Aborted');
        }
        return data;
      });
    },
    throwOnError: (error: Error) => onError(error, defaultOptions, controllerRef?.current, options),
    enabled: !disabled && !disabledParam,
    refetchOnMount: options?.refetchOnMount,
    retry: (failureCount, error: Error) => {
      if ((error as ResponseError)?.status === 404) {
        return false;
      }
      return failureCount < 4;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * **This function must be used only when creating the hook that will be used in the components**
 *
 * Used for hooks that require only an ID as parameter
 *
 * @example
 * ```typescript
 * // In a file like `src/hooks/services/brands.ts` do:
 * const useBrand = HookBuilder.getByIdHook(BrandsService.getById, {
 *   errorMessage: 'There was an error fetching the brand',
 * });
 *
 * // Then in the component we use it like this:
 * const { data: brand } = useBrand(id);
 * // Or:
 * const { data: brand } = useBrand(id, {
 *   // Custom options here...
 *   errorMessage: 'Custom error message',
 * });
 * ```
 */
const getByIdHook =
  <Data>(queryKey: QueryKey, queryFn: QueryByIdFn<Data, number>, defaultOptions: DefaultOptions) =>
  (id?: number | undefined, fetchOptions: FetchHookOptions = DEFAULT_EMPTY_PARAMS) =>
    useEnrichedQuery(queryKey, queryFn, id ?? 0, !id, fetchOptions, defaultOptions);

/**
 * **This function must be used only when creating the hook that will be used in the components**
 *
 * Used for hooks that require only IDs as parameter
 *
 * @example
 * ```typescript
 * // In a file like `src/hooks/services/brands.ts` do:
 * const useBrand = HookBuilder.getByIdsHook(BrandsService.getById, {
 *   errorMessage: 'There was an error fetching the brand',
 * });
 *
 * // Then in the component we use it like this:
 * const { data: brand } = useBrand([id]);
 * // Or:
 * const { data: brand } = useBrand([id], {
 *   // Custom options here...
 *   errorMessage: 'Custom error message',
 * });
 * ```
 */
const getByIdsHook =
  <Data, Param extends number[] = number[]>(
    queryKey: QueryKey,
    queryFn: QueryByIdFn<Data, Param>,
    defaultOptions: DefaultOptions,
  ) =>
  (id?: Param, fetchOptions: FetchHookOptions = DEFAULT_EMPTY_PARAMS) =>
    useEnrichedQuery(
      queryKey,
      queryFn,
      id ?? (new Array<number>() as Param),
      !id || id.some((i) => !i),
      fetchOptions,
      defaultOptions,
    );

/**
 * **This function must be used only when creating the hook that will be used in the components**
 *
 * @example
 * ```typescript
 * // In a file like `src/hooks/services/brands.ts` do:
 * const useBrand = HookBuilder.regularHook(BrandsService.getById, {
 *   errorMessage: 'There was an error fetching the brand',
 * });
 *
 * // Then in the component we use it like this:
 * const { data: brand } = useBrand([id]);
 * // Or:
 * const { data: brand } = useBrand([id], {
 *   // Custom options here...
 *   errorMessage: 'Custom error message',
 * });
 * ```
 */
const regularHook = <Data extends Result, Params extends any[] = any[]>(
  queryKey: QueryKey,
  queryFn: QueryRegularFn<Data, Params>,
  defaultOptions: RegularHookOptions<Params>,
) => {
  // If we didn't have optional parameters in hooks we could use something like this:
  // `type Args = [...Params, FetchHookOptions<Result>?];`
  // but since we cannot know if optional parameters are passed or not
  // (and same happens with `options`) we can't use this approach

  const disabledByParams = (params: Params) => {
    if (!defaultOptions.disable) {
      return false;
    }

    return defaultOptions.disable(params);
  };

  return (params: Params, fetchOptions: FetchHookOptions = DEFAULT_EMPTY_PARAMS) =>
    useEnrichedQuery(
      queryKey,
      queryFn,
      params,
      disabledByParams(params),
      fetchOptions,
      defaultOptions,
    );
};

/**
 * **This function must be used only when creating the hook that will be used in the components**
 *
 * Used for hooks that don't require any parameters
 *
 * @example
 * ```typescript
 * // In a file like `src/hooks/services/brands.ts` do:
 * const useBrands = HookBuilder.noParamsHook(BrandsService.search, {
 *   errorMessage: 'There was an error fetching the brands',
 * });
 *
 * // Then in the component we use it like this:
 * const { data: brands } = useBrands();
 * // Or:
 * const { data: brands } = useBrands({
 *   // Custom options here...
 *   errorMessage: 'Custom error message',
 * });
 * ```
 */
const noParamsHook =
  <Data extends Result, MapKey extends keyof any = keyof any, OptionValue = any>(
    queryKey: QueryKey,
    queryFn: QueryNoParamsFn<Data>,
    defaultOptions: NoParamsHookOptions<MapKey, OptionValue>,
  ) =>
  (
    fetchOptions: FetchHookOptions = DEFAULT_EMPTY_PARAMS,
    optionsSettings: OptionsSettings = DEFAULT_EMPTY_PARAMS,
  ) => {
    const result = useEnrichedQuery(
      queryKey,
      (_, signal?: AbortSignal) => queryFn(signal),
      undefined,
      false,
      fetchOptions,
      defaultOptions,
    );

    const arrayData = Array.isArray(result.data) ? result.data : undefined;

    // Build `map` property
    const map = useMapSettings(arrayData, defaultOptions.parseToMap);

    // Build `options` property
    const optionsArray = useOptionsSettings(
      arrayData,
      defaultOptions.parseToOptions,
      optionsSettings,
    );

    return { ...result, map, options: optionsArray };
  };

/**
 * **This function must be used only when creating the hook that will be used in the components**
 *
 * Used for hooks that require search parameters
 *
 * @example
 * ```typescript
 * // In a file like `src/hooks/services/user.ts` do:
 * const useUsers = HookBuilder.searchParamsHook(UserService.search, {
 *   errorMessage: 'There was an error fetching the users',
 * });
 *
 * // Then in the component we use it like this:
 * const { data: users } = useUsers({ status });
 * // Or:
 * const { data: users } = useUsers({ status }, {
 *   // Custom options here...
 *   errorMessage: 'Custom error message',
 * });
 * ```
 */
const searchParamsHook = <
  Data extends Result,
  Params extends object = object,
  MapKey extends keyof any = keyof any,
  OptionValue = any,
>(
  queryKey: QueryKey,
  queryFn: QuerySearchFn<Data[], Params>,
  defaultOptions: SearchHookOptions<MapKey, OptionValue>,
) => {
  const disabledByParams = (params: Params) => {
    if (!defaultOptions.disable) {
      return false;
    }

    return defaultOptions.disable(params);
  };

  return (
    params = DEFAULT_EMPTY_PARAMS as Params,
    fetchOptions: FetchHookOptions = DEFAULT_EMPTY_PARAMS,
    optionsSettings: OptionsSettings = DEFAULT_EMPTY_PARAMS,
  ) => {
    const result = useEnrichedQuery(
      queryKey,
      queryFn,
      params,
      disabledByParams(params),
      fetchOptions,
      defaultOptions,
    );

    // Build `map` property
    const map = useMapSettings(result.data, defaultOptions.parseToMap);

    // Build `options` property
    const optionsArray = useOptionsSettings(
      result.data,
      defaultOptions.parseToOptions,
      optionsSettings,
    );

    return { ...result, map, options: optionsArray };
  };
};

const HookBuilder = {
  getByIdHook,
  getByIdsHook,
  searchParamsHook,
  regularHook,
  noParamsHook,
};

export default HookBuilder;
